import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import {
  Database,
  RefreshCw,
  Camera,
  GitCompare,
  Trash2,
  ArrowRight,
  ArrowLeft,
  ArrowLeftRight,
  AlertCircle,
  Search,
  ArrowUpDown,
  X,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

let BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
if (BACKEND_URL && !BACKEND_URL.startsWith('http')) {
  BACKEND_URL = `https://${BACKEND_URL}`;
}
if (!BACKEND_URL) {
  BACKEND_URL = window.location.origin;
}
const API = `${BACKEND_URL}/api`;

const directionIcon = (dir) => {
  if (dir === "hubspot_to_bc") return <ArrowRight className="w-3.5 h-3.5 text-[#FF7A59]" />;
  if (dir === "bc_to_hubspot") return <ArrowLeft className="w-3.5 h-3.5 text-[#00B7C3]" />;
  return <ArrowLeftRight className="w-3.5 h-3.5 text-indigo-500" />;
};

export default function DataBrowser() {
  const { user } = useAuth();
  const [objectMappings, setObjectMappings] = useState([]);
  const [selectedMappingId, setSelectedMappingId] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [fieldSearch, setFieldSearch] = useState("");
  const [sortAlpha, setSortAlpha] = useState(true);

  // BC Explorer state
  const [bcSchema, setBcSchema] = useState(null);
  const [bcSchemaLoading, setBcSchemaLoading] = useState(false);
  const [selectedBcEntity, setSelectedBcEntity] = useState("");
  const [bcRawRecords, setBcRawRecords] = useState(null);
  const [bcRawLoading, setBcRawLoading] = useState(false);
  const [selectedBcField, setSelectedBcField] = useState(null);
  const [bcDebugInfo, setBcDebugInfo] = useState(null);
  const [bcDebugLoading, setBcDebugLoading] = useState(false);
  const [bcFieldSearch, setBcFieldSearch] = useState("");

  useEffect(() => {
    fetchObjectMappings();
  }, []);

  // Fetch BC schema (all entities and fields)
  const fetchBcSchema = async () => {
    setBcSchemaLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/bc/schema`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBcSchema(response.data);
      if (response.data.error) {
        toast.error(response.data.error);
      }
    } catch (error) {
      toast.error("Erreur chargement du schéma BC: " + (error.response?.data?.detail || error.message));
    } finally {
      setBcSchemaLoading(false);
    }
  };

  // Fetch raw records for a specific BC entity
  const fetchBcRawRecords = async (entityType) => {
    if (!entityType) return;
    setBcRawLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/bc/raw-records/${entityType}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 50 },
      });
      setBcRawRecords(response.data);
    } catch (error) {
      toast.error("Erreur chargement des records BC: " + (error.response?.data?.detail || error.message));
      setBcRawRecords(null);
    } finally {
      setBcRawLoading(false);
    }
  };

  // Fetch debug info for BC metadata
  const fetchBcDebug = async () => {
    const entityToDebug = selectedBcEntity || "customers";
    setBcDebugLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/bc/debug-metadata/${entityToDebug}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBcDebugInfo({ entity: entityToDebug, ...response.data });
    } catch (error) {
      setBcDebugInfo({ entity: entityToDebug, error: error.response?.data?.detail || error.message });
    } finally {
      setBcDebugLoading(false);
    }
  };

  // Load snapshot from localStorage when mapping changes
  useEffect(() => {
    if (selectedMappingId) {
      const saved = localStorage.getItem(`data-snapshot-${selectedMappingId}`);
      if (saved) {
        try {
          setSnapshot(JSON.parse(saved));
        } catch {
          setSnapshot(null);
        }
      } else {
        setSnapshot(null);
      }
      setCompareMode(false);
    }
  }, [selectedMappingId]);

  const fetchObjectMappings = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/object-mappings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const mappings = Array.isArray(response.data) ? response.data : [];
      setObjectMappings(mappings);
      if (mappings.length > 0) {
        setSelectedMappingId(mappings[0].id);
      }
    } catch (error) {
      toast.error("Erreur chargement des object mappings");
    }
  };

  const fetchData = useCallback(async () => {
    if (!selectedMappingId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/data-browser/${selectedMappingId}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 100 },
      });
      setData(response.data);
      if (response.data.errors) {
        response.data.errors.forEach(e => toast.warning(e));
      }
    } catch (error) {
      toast.error("Erreur chargement des données: " + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  }, [selectedMappingId]);

  useEffect(() => {
    if (selectedMappingId) {
      fetchData();
    }
  }, [selectedMappingId, fetchData]);

  const takeSnapshot = () => {
    if (!data) return;
    const snap = {
      timestamp: new Date().toISOString(),
      hubspot_records: data.hubspot_records,
      bc_records: data.bc_records,
      field_mappings: data.field_mappings,
    };
    localStorage.setItem(`data-snapshot-${selectedMappingId}`, JSON.stringify(snap));
    setSnapshot(snap);
    toast.success("Snapshot sauvegardé");
  };

  const deleteSnapshot = () => {
    localStorage.removeItem(`data-snapshot-${selectedMappingId}`);
    setSnapshot(null);
    setCompareMode(false);
    toast.info("Snapshot supprimé");
  };

  const toggleRow = (key) => {
    setExpandedRows(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const selectedMapping = objectMappings.find(m => m.id === selectedMappingId);

  const formatValue = (val) => {
    if (val === null || val === undefined || val === "") return <span className="text-slate-400 italic">—</span>;
    if (typeof val === "object") return JSON.stringify(val);
    const s = String(val);
    return s.length > 60 ? s.slice(0, 57) + "…" : s;
  };

  const hasChanged = (oldVal, newVal) => {
    const a = oldVal === null || oldVal === undefined ? "" : String(oldVal);
    const b = newVal === null || newVal === undefined ? "" : String(newVal);
    return a !== b;
  };

  // Filter and sort fields based on search + alphabetical toggle
  const getFilteredFields = (fields, side) => {
    let filtered = fields;
    if (fieldSearch.trim()) {
      const q = fieldSearch.toLowerCase();
      filtered = fields.filter(f => {
        const label = (side === "hubspot" ? f.hubspot_field_label : f.bc_field_label) || "";
        const name = (side === "hubspot" ? f.hubspot_field : f.bc_field) || "";
        return label.toLowerCase().includes(q) || name.toLowerCase().includes(q);
      });
    }
    if (sortAlpha) {
      filtered = [...filtered].sort((a, b) => {
        const la = (side === "hubspot" ? a.hubspot_field_label : a.bc_field_label) || "";
        const lb = (side === "hubspot" ? b.hubspot_field_label : b.bc_field_label) || "";
        return la.localeCompare(lb, "fr");
      });
    }
    return filtered;
  };

  const renderRecordsTable = (records, fields, side, snapshotRecords) => {
    if (!records || records.length === 0) {
      return (
        <div className="text-center py-8 text-slate-500">
          <Database className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p>Aucun enregistrement</p>
        </div>
      );
    }

    const sortedFields = getFilteredFields(fields, side);
    if (sortedFields.length === 0) {
      return (
        <div className="text-center py-8 text-slate-500">
          <Search className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p>Aucun champ ne correspond à « {fieldSearch} »</p>
        </div>
      );
    }
    const fieldKeys = sortedFields.map(f => side === "hubspot" ? f.hubspot_field : f.bc_field);
    const fieldLabels = sortedFields.map(f => side === "hubspot" ? f.hubspot_field_label : f.bc_field_label);

    // Build snapshot lookup by _id
    const snapMap = {};
    if (compareMode && snapshotRecords) {
      snapshotRecords.forEach(r => { snapMap[r._id] = r; });
    }

    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="text-left px-3 py-2 font-medium text-slate-600 w-8">#</th>
              <th className="text-left px-3 py-2 font-medium text-slate-600">ID</th>
              {fieldLabels.map((label, i) => (
                <th key={i} className="text-left px-3 py-2 font-medium text-slate-600">
                  <div className="flex items-center gap-1.5">
                    {directionIcon(sortedFields[i].direction)}
                    <span className="truncate max-w-[150px]" title={label}>{label}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {records.map((record, idx) => {
              const snapRecord = snapMap[record._id];
              return (
                <tr key={record._id || idx} className="border-b border-slate-100 hover:bg-slate-50/50">
                  <td className="px-3 py-2 text-slate-400">{idx + 1}</td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-500 max-w-[120px] truncate" title={record._id}>
                    {record._id}
                  </td>
                  {fieldKeys.map((key, i) => {
                    const currentVal = record[key];
                    const changed = compareMode && snapRecord && hasChanged(snapRecord[key], currentVal);
                    return (
                      <td key={i} className={`px-3 py-2 ${changed ? "bg-amber-50" : ""}`}>
                        <div className="flex flex-col">
                          <span className={changed ? "font-medium text-amber-800" : "text-slate-700"}>
                            {formatValue(currentVal)}
                          </span>
                          {changed && (
                            <span className="text-xs text-slate-400 line-through">
                              {formatValue(snapRecord[key])}
                            </span>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // Summary stats for compare mode
  const computeChanges = (records, snapshotRecords, fields, side) => {
    if (!records || !snapshotRecords) return null;
    const fieldKeys = fields.map(f => side === "hubspot" ? f.hubspot_field : f.bc_field);
    const snapMap = {};
    snapshotRecords.forEach(r => { snapMap[r._id] = r; });

    let changedFields = 0;
    let changedRecords = 0;
    records.forEach(r => {
      const snap = snapMap[r._id];
      if (!snap) { changedRecords++; return; }
      let recordChanged = false;
      fieldKeys.forEach(k => {
        if (hasChanged(snap[k], r[k])) { changedFields++; recordChanged = true; }
      });
      if (recordChanged) changedRecords++;
    });
    return { changedFields, changedRecords, newRecords: records.filter(r => !snapMap[r._id]).length };
  };

  // State for main tab
  const [mainTab, setMainTab] = useState("mapped");

  // Handle main tab change
  const handleMainTabChange = (tab) => {
    setMainTab(tab);
    if (tab === "bc-explorer" && !bcSchema) {
      fetchBcSchema();
    }
  };

  // Handle BC entity selection
  const handleBcEntitySelect = (entityType) => {
    setSelectedBcEntity(entityType);
    setSelectedBcField(null);
    setBcFieldSearch("");
    fetchBcRawRecords(entityType);
  };

  // Handle BC field selection
  const handleBcFieldSelect = (field) => {
    setSelectedBcField(selectedBcField?.name === field.name ? null : field);
  };

  return (
    <div className="space-y-6 animate-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Explorateur de données</h1>
          <p className="text-slate-500 mt-1">
            Visualiser le contenu des champs mappés dans HubSpot et Business Central
          </p>
        </div>
        {mainTab === "mapped" && (
          <div className="flex items-center gap-2">
            {snapshot && (
              <>
                <Button
                  variant={compareMode ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCompareMode(!compareMode)}
                >
                  <GitCompare className="w-4 h-4 mr-1.5" />
                  {compareMode ? "Mode comparaison" : "Comparer"}
                </Button>
                <Button variant="ghost" size="sm" onClick={deleteSnapshot} title="Supprimer le snapshot">
                  <Trash2 className="w-4 h-4 text-slate-400" />
                </Button>
              </>
            )}
            <Button variant="outline" size="sm" onClick={takeSnapshot} disabled={!data}>
              <Camera className="w-4 h-4 mr-1.5" />
              Snapshot
            </Button>
            <Button variant="outline" size="sm" onClick={fetchData} disabled={loading || !selectedMappingId}>
              <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
              Actualiser
            </Button>
          </div>
        )}
        {mainTab === "bc-explorer" && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchBcSchema} disabled={bcSchemaLoading}>
              <RefreshCw className={`w-4 h-4 mr-1.5 ${bcSchemaLoading ? "animate-spin" : ""}`} />
              Actualiser le schéma BC
            </Button>
            <Button variant="outline" size="sm" onClick={fetchBcDebug} disabled={bcDebugLoading}>
              <Search className={`w-4 h-4 mr-1.5 ${bcDebugLoading ? "animate-spin" : ""}`} />
              Debug metadata
            </Button>
          </div>
        )}
      </div>

      {/* Main tabs */}
      <Tabs value={mainTab} onValueChange={handleMainTabChange} className="space-y-4">
        <TabsList>
          <TabsTrigger value="mapped" className="gap-1.5">
            <Database className="w-4 h-4" />
            Données mappées
          </TabsTrigger>
          <TabsTrigger value="bc-explorer" className="gap-1.5">
            <span className="w-2 h-2 rounded-full bg-[#00B7C3]" />
            BC Explorer
          </TabsTrigger>
        </TabsList>

        {/* Mapped Data Tab */}
        <TabsContent value="mapped" className="space-y-4">

      {/* Object mapping selector */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1">
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Object Mapping</label>
              <Select value={selectedMappingId} onValueChange={setSelectedMappingId}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un mapping..." />
                </SelectTrigger>
                <SelectContent>
                  {objectMappings.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.hubspot_object_label || m.hubspot_object} ↔ {m.bc_entity_label || m.bc_entity}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {snapshot && (
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">
                  <Camera className="w-3 h-3 mr-1" />
                  Snapshot: {new Date(snapshot.timestamp).toLocaleString("fr-CA")}
                </Badge>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Compare mode summary */}
      {compareMode && snapshot && data && (
        <Card className="border-amber-200 bg-amber-50/30">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2 mb-2">
              <GitCompare className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-medium text-amber-800">Mode comparaison actif</span>
            </div>
            <p className="text-xs text-amber-700">
              Les valeurs modifiées depuis le snapshot du{" "}
              {new Date(snapshot.timestamp).toLocaleString("fr-CA")} sont surlignées en jaune.
              L'ancienne valeur est barrée en dessous.
            </p>
            <div className="flex gap-4 mt-3">
              {(() => {
                const hsChanges = computeChanges(data.hubspot_records, snapshot.hubspot_records, data.field_mappings, "hubspot");
                const bcChanges = computeChanges(data.bc_records, snapshot.bc_records, data.field_mappings, "bc");
                return (
                  <>
                    {hsChanges && (
                      <div className="text-xs text-slate-600">
                        <span className="font-medium text-[#FF7A59]">HubSpot:</span>{" "}
                        {hsChanges.changedRecords} enregistrement(s) modifié(s), {hsChanges.changedFields} champ(s)
                      </div>
                    )}
                    {bcChanges && (
                      <div className="text-xs text-slate-600">
                        <span className="font-medium text-[#00B7C3]">BC:</span>{" "}
                        {bcChanges.changedRecords} enregistrement(s) modifié(s), {bcChanges.changedFields} champ(s)
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Errors */}
      {data?.errors && (
        <Card className="border-red-200 bg-red-50/30">
          <CardContent className="pt-4 pb-4 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-500 mt-0.5" />
            <div>
              {data.errors.map((e, i) => (
                <p key={i} className="text-sm text-red-700">{e}</p>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Data tables */}
      {loading && !data && (
        <Card>
          <CardContent className="py-12 text-center">
            <RefreshCw className="w-6 h-6 mx-auto mb-2 animate-spin text-slate-400" />
            <p className="text-slate-500">Chargement des données...</p>
          </CardContent>
        </Card>
      )}

      {data && data.field_mappings && data.field_mappings.length > 0 && (
        <>
        {/* Search and sort controls */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Rechercher un champ..."
              value={fieldSearch}
              onChange={(e) => setFieldSearch(e.target.value)}
              className="pl-9 h-9"
            />
            {fieldSearch && (
              <button
                onClick={() => setFieldSearch("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <Button
            variant={sortAlpha ? "default" : "outline"}
            size="sm"
            onClick={() => setSortAlpha(!sortAlpha)}
            title={sortAlpha ? "Tri alphabétique activé" : "Ordre original"}
          >
            <ArrowUpDown className="w-4 h-4 mr-1.5" />
            A→Z
          </Button>
          {fieldSearch && (
            <Badge variant="secondary" className="text-xs">
              {getFilteredFields(data.field_mappings, "hubspot").length} / {data.field_mappings.length} champs
            </Badge>
          )}
        </div>

        <Tabs defaultValue="hubspot" className="space-y-4">
          <TabsList>
            <TabsTrigger value="hubspot" className="gap-1.5">
              <span className="w-2 h-2 rounded-full bg-[#FF7A59]" />
              HubSpot
              <Badge variant="secondary" className="ml-1 text-xs">
                {data.hubspot_records?.length || 0}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="bc" className="gap-1.5">
              <span className="w-2 h-2 rounded-full bg-[#00B7C3]" />
              Business Central
              <Badge variant="secondary" className="ml-1 text-xs">
                {data.bc_records?.length || 0}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="side-by-side" className="gap-1.5">
              <ArrowLeftRight className="w-3.5 h-3.5" />
              Côte à côte
            </TabsTrigger>
            <TabsTrigger value="compare" className="gap-1.5">
              <GitCompare className="w-3.5 h-3.5" />
              Compare
            </TabsTrigger>
          </TabsList>

          <TabsContent value="hubspot">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#FF7A59]" />
                  Enregistrements HubSpot — {selectedMapping?.hubspot_object_label || selectedMapping?.hubspot_object}
                </CardTitle>
                <CardDescription>
                  {data.hubspot_records?.length || 0} enregistrement(s) • {data.field_mappings.length} champ(s) mappé(s)
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                {renderRecordsTable(data.hubspot_records, data.field_mappings, "hubspot", snapshot?.hubspot_records)}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="bc">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#00B7C3]" />
                  Enregistrements Business Central — {selectedMapping?.bc_entity_label || selectedMapping?.bc_entity}
                </CardTitle>
                <CardDescription>
                  {data.bc_records?.length || 0} enregistrement(s) • {data.field_mappings.length} champ(s) mappé(s)
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                {renderRecordsTable(data.bc_records, data.field_mappings, "bc", snapshot?.bc_records)}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="side-by-side">
            {data.matched_pairs && data.matched_pairs.length > 0 ? (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">
                    Comparaison par clé de correspondance
                  </CardTitle>
                  <CardDescription>
                    {data.matched_pairs.filter(p => p.hs && p.bc).length} correspondances,{" "}
                    {data.matched_pairs.filter(p => p.hs && !p.bc).length} HubSpot seul,{" "}
                    {data.matched_pairs.filter(p => !p.hs && p.bc).length} BC seul
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50">
                        <th className="text-left px-3 py-2 font-medium text-slate-600 w-8">#</th>
                        <th className="text-left px-3 py-2 font-medium text-slate-600">Clé</th>
                        <th className="text-left px-3 py-2 font-medium text-[#FF7A59]">
                          HubSpot {data.matching_keys?.hubspot && `(${data.matching_keys.hubspot})`}
                        </th>
                        <th className="text-left px-3 py-2 font-medium text-[#00B7C3]">
                          BC {data.matching_keys?.bc && `(${data.matching_keys.bc})`}
                        </th>
                        <th className="text-left px-3 py-2 font-medium text-slate-600">Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.matched_pairs.map((pair, idx) => (
                        <tr key={idx} className={`border-b border-slate-100 hover:bg-slate-50/50 ${!pair.hs || !pair.bc ? "bg-amber-50/30" : ""}`}>
                          <td className="px-3 py-2 text-slate-400">{idx + 1}</td>
                          <td className="px-3 py-2 font-medium text-slate-700 max-w-[200px] truncate" title={pair.match_key}>
                            {pair.match_key || <span className="text-slate-400 italic">—</span>}
                          </td>
                          <td className="px-3 py-2 font-mono text-xs text-slate-500">
                            {pair.hs ? pair.hs._id : <span className="text-amber-600 italic">Non trouvé</span>}
                          </td>
                          <td className="px-3 py-2 font-mono text-xs text-slate-500 max-w-[120px] truncate" title={pair.bc?._id}>
                            {pair.bc ? pair.bc._id : <span className="text-amber-600 italic">Non trouvé</span>}
                          </td>
                          <td className="px-3 py-2">
                            {pair.hs && pair.bc ? (
                              <Badge variant="secondary" className="bg-green-100 text-green-700 text-xs">Match</Badge>
                            ) : pair.hs ? (
                              <Badge variant="secondary" className="bg-[#FF7A59]/10 text-[#FF7A59] text-xs">HS seul</Badge>
                            ) : (
                              <Badge variant="secondary" className="bg-[#00B7C3]/10 text-[#00B7C3] text-xs">BC seul</Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-[#FF7A59]" />
                      HubSpot — {selectedMapping?.hubspot_object_label || selectedMapping?.hubspot_object}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {renderRecordsTable(data.hubspot_records, data.field_mappings, "hubspot", snapshot?.hubspot_records)}
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-[#00B7C3]" />
                      BC — {selectedMapping?.bc_entity_label || selectedMapping?.bc_entity}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {renderRecordsTable(data.bc_records, data.field_mappings, "bc", snapshot?.bc_records)}
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          <TabsContent value="compare">
            {data.matched_pairs && data.matched_pairs.length > 0 ? (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <GitCompare className="w-4 h-4" />
                    Comparaison des valeurs HubSpot ↔ BC
                  </CardTitle>
                  <CardDescription>
                    Comparez les valeurs de chaque champ mappé entre HubSpot et Business Central.
                    Les différences sont surlignées en couleur.
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-3">
                    {data.matched_pairs.filter(p => p.hs && p.bc).map((pair, pairIdx) => {
                      const pairKey = `compare-pair-${pairIdx}`;
                      const isExpanded = expandedRows[pairKey];
                      const filteredFields = getFilteredFields(data.field_mappings, "hubspot");

                      // Count differences for this pair
                      let diffCount = 0;
                      filteredFields.forEach(fm => {
                        const hsVal = pair.hs[fm.hubspot_field];
                        const bcVal = pair.bc[fm.bc_field];
                        const hsStr = hsVal === null || hsVal === undefined ? "" : String(hsVal);
                        const bcStr = bcVal === null || bcVal === undefined ? "" : String(bcVal);
                        if (hsStr !== bcStr) diffCount++;
                      });

                      return (
                        <div key={pairIdx} className="border rounded-lg overflow-hidden">
                          {/* Header row - clickable to expand */}
                          <button
                            onClick={() => toggleRow(pairKey)}
                            className="w-full px-4 py-3 bg-slate-50 hover:bg-slate-100 flex items-center justify-between transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-medium text-slate-700">
                                {pair.match_key || `Record ${pairIdx + 1}`}
                              </span>
                              {diffCount > 0 ? (
                                <Badge className="bg-amber-100 text-amber-700 text-xs">
                                  {diffCount} différence{diffCount > 1 ? "s" : ""}
                                </Badge>
                              ) : (
                                <Badge className="bg-green-100 text-green-700 text-xs">
                                  Identique
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-4 text-xs text-slate-500">
                              <span>
                                <span className="text-[#FF7A59]">HS:</span> {pair.hs._id}
                              </span>
                              <span>
                                <span className="text-[#00B7C3]">BC:</span> {String(pair.bc._id).slice(0, 20)}{String(pair.bc._id).length > 20 ? "..." : ""}
                              </span>
                              <span className={`transform transition-transform ${isExpanded ? "rotate-180" : ""}`}>
                                ▼
                              </span>
                            </div>
                          </button>

                          {/* Expanded comparison table */}
                          {isExpanded && (
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b border-slate-200 bg-white">
                                    <th className="text-left px-4 py-2 font-medium text-slate-600 w-[200px]">Champ</th>
                                    <th className="text-left px-4 py-2 font-medium text-[#FF7A59]">
                                      <span className="flex items-center gap-1.5">
                                        <span className="w-2 h-2 rounded-full bg-[#FF7A59]" />
                                        HubSpot
                                      </span>
                                    </th>
                                    <th className="text-left px-4 py-2 font-medium text-[#00B7C3]">
                                      <span className="flex items-center gap-1.5">
                                        <span className="w-2 h-2 rounded-full bg-[#00B7C3]" />
                                        Business Central
                                      </span>
                                    </th>
                                    <th className="text-left px-4 py-2 font-medium text-slate-600 w-[100px]">Statut</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {filteredFields.map((fm, fIdx) => {
                                    const hsVal = pair.hs[fm.hubspot_field];
                                    const bcVal = pair.bc[fm.bc_field];
                                    const hsStr = hsVal === null || hsVal === undefined ? "" : String(hsVal);
                                    const bcStr = bcVal === null || bcVal === undefined ? "" : String(bcVal);
                                    const isDiff = hsStr !== bcStr;
                                    const hsEmpty = hsStr === "";
                                    const bcEmpty = bcStr === "";

                                    let statusBadge;
                                    if (!isDiff) {
                                      statusBadge = <Badge variant="outline" className="text-xs text-slate-500">Identique</Badge>;
                                    } else if (hsEmpty && !bcEmpty) {
                                      statusBadge = <Badge className="bg-[#00B7C3]/10 text-[#00B7C3] text-xs">BC seul</Badge>;
                                    } else if (!hsEmpty && bcEmpty) {
                                      statusBadge = <Badge className="bg-[#FF7A59]/10 text-[#FF7A59] text-xs">HS seul</Badge>;
                                    } else {
                                      statusBadge = <Badge className="bg-amber-100 text-amber-700 text-xs">Différent</Badge>;
                                    }

                                    return (
                                      <tr
                                        key={fIdx}
                                        className={`border-b border-slate-100 ${isDiff ? "bg-amber-50/40" : ""}`}
                                      >
                                        <td className="px-4 py-2">
                                          <div className="flex items-center gap-1.5">
                                            {directionIcon(fm.direction)}
                                            <span className="font-medium text-slate-700 truncate" title={fm.hubspot_field_label}>
                                              {fm.hubspot_field_label || fm.hubspot_field}
                                            </span>
                                          </div>
                                          <div className="text-xs text-slate-400 mt-0.5">
                                            {fm.hubspot_field} ↔ {fm.bc_field}
                                          </div>
                                        </td>
                                        <td className={`px-4 py-2 ${isDiff && !hsEmpty ? "font-medium" : ""}`}>
                                          {hsEmpty ? (
                                            <span className="text-slate-400 italic">(vide)</span>
                                          ) : (
                                            <span className={isDiff ? "text-[#FF7A59]" : "text-slate-700"}>
                                              {hsStr.length > 80 ? hsStr.slice(0, 77) + "..." : hsStr}
                                            </span>
                                          )}
                                        </td>
                                        <td className={`px-4 py-2 ${isDiff && !bcEmpty ? "font-medium" : ""}`}>
                                          {bcEmpty ? (
                                            <span className="text-slate-400 italic">(vide)</span>
                                          ) : (
                                            <span className={isDiff ? "text-[#00B7C3]" : "text-slate-700"}>
                                              {bcStr.length > 80 ? bcStr.slice(0, 77) + "..." : bcStr}
                                            </span>
                                          )}
                                        </td>
                                        <td className="px-4 py-2">
                                          {statusBadge}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Summary of unmatched records */}
                  {(data.matched_pairs.filter(p => p.hs && !p.bc).length > 0 ||
                    data.matched_pairs.filter(p => !p.hs && p.bc).length > 0) && (
                    <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                      <h4 className="text-sm font-medium text-slate-700 mb-2">Enregistrements non-matchés</h4>
                      <div className="flex gap-4 text-sm">
                        {data.matched_pairs.filter(p => p.hs && !p.bc).length > 0 && (
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-[#FF7A59]" />
                            <span className="text-slate-600">
                              {data.matched_pairs.filter(p => p.hs && !p.bc).length} HubSpot sans correspondance BC
                            </span>
                          </div>
                        )}
                        {data.matched_pairs.filter(p => !p.hs && p.bc).length > 0 && (
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-[#00B7C3]" />
                            <span className="text-slate-600">
                              {data.matched_pairs.filter(p => !p.hs && p.bc).length} BC sans correspondance HubSpot
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <GitCompare className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  <p className="text-slate-500">Aucune correspondance trouvée entre HubSpot et BC.</p>
                  <p className="text-sm text-slate-400 mt-1">
                    Assurez-vous d'avoir configuré les clés de correspondance dans les object mappings.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
        </>
      )}

      {data && data.field_mappings && data.field_mappings.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Database className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            <p className="text-slate-500">Aucun mapping de champs actif pour cet objet.</p>
            <p className="text-sm text-slate-400 mt-1">Configurez des mappings dans Field Mapping d'abord.</p>
          </CardContent>
        </Card>
      )}
        </TabsContent>

        {/* BC Explorer Tab */}
        <TabsContent value="bc-explorer" className="space-y-4">
          {/* Debug info display */}
          {bcDebugInfo && (
            <Card className="border-amber-200 bg-amber-50/30">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base text-amber-800">Debug Metadata BC - {bcDebugInfo.entity || "customers"}</CardTitle>
                  <button
                    onClick={() => setBcDebugInfo(null)}
                    className="text-amber-400 hover:text-amber-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-xs text-amber-700 mb-2">Copie ce texte et partage-le pour investigation:</p>
                <textarea
                  readOnly
                  value={JSON.stringify(bcDebugInfo, null, 2)}
                  className="w-full h-64 font-mono text-xs p-2 border border-amber-300 rounded bg-white resize-y"
                  onClick={(e) => e.target.select()}
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => {
                    navigator.clipboard.writeText(JSON.stringify(bcDebugInfo, null, 2));
                    toast.success("Copié dans le presse-papiers!");
                  }}
                >
                  Copier dans le presse-papiers
                </Button>
              </CardContent>
            </Card>
          )}

          {bcSchemaLoading && !bcSchema && (
            <Card>
              <CardContent className="py-12 text-center">
                <RefreshCw className="w-6 h-6 mx-auto mb-2 animate-spin text-slate-400" />
                <p className="text-slate-500">Chargement du schéma BC...</p>
              </CardContent>
            </Card>
          )}

          {bcSchema && (
            <>
              {/* Schema summary */}
              <Card className="border-[#00B7C3]/20 bg-[#00B7C3]/5">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-3 h-3 rounded-full bg-[#00B7C3]" />
                    <span className="text-sm font-medium text-slate-700">
                      Schéma Business Central
                    </span>
                  </div>
                  <p className="text-xs text-slate-600">
                    {bcSchema.entities?.length || 0} entités découvertes via l'API BC.
                    Sélectionnez une entité pour voir tous ses champs et quelques enregistrements.
                  </p>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Entity list */}
                <Card className="lg:col-span-1">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Entités BC</CardTitle>
                    <CardDescription>
                      {bcSchema.entities?.length || 0} entités disponibles
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0 max-h-[500px] overflow-y-auto">
                    <div className="space-y-1">
                      {bcSchema.entities?.map((entity) => (
                        <button
                          key={entity.name}
                          onClick={() => handleBcEntitySelect(entity.name)}
                          className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                            selectedBcEntity === entity.name
                              ? "bg-[#00B7C3]/10 text-[#00B7C3] font-medium"
                              : "hover:bg-slate-100 text-slate-700"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span>{entity.label || entity.name}</span>
                            <Badge variant="secondary" className="text-xs">
                              {entity.field_count} champs
                            </Badge>
                          </div>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Fields and records panel */}
                <Card className="lg:col-span-2">
                  {!selectedBcEntity ? (
                    <CardContent className="py-12 text-center">
                      <Database className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                      <p className="text-slate-500">Sélectionnez une entité pour voir ses champs</p>
                    </CardContent>
                  ) : (
                    <>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full bg-[#00B7C3]" />
                          {selectedBcEntity}
                        </CardTitle>
                        <CardDescription>
                          {bcSchema.entities?.find(e => e.name === selectedBcEntity)?.field_count || 0} champs trouvés
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="pt-0 space-y-4">
                        {/* Fields list */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-medium text-slate-700">Tous les champs <span className="font-normal text-slate-400">(cliquez pour voir les valeurs)</span></h4>
                          </div>
                          <div className="relative mb-2">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                              placeholder="Rechercher un champ..."
                              value={bcFieldSearch}
                              onChange={(e) => setBcFieldSearch(e.target.value)}
                              className="pl-8 h-8 text-sm"
                            />
                            {bcFieldSearch && (
                              <button
                                onClick={() => setBcFieldSearch("")}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                          <div className="max-h-[250px] overflow-y-auto border rounded-md">
                            <table className="w-full text-sm">
                              <thead className="sticky top-0 bg-slate-50 border-b">
                                <tr>
                                  <th className="text-left px-3 py-2 font-medium text-slate-600">#</th>
                                  <th className="text-left px-3 py-2 font-medium text-slate-600">Nom du champ</th>
                                  <th className="text-left px-3 py-2 font-medium text-slate-600">Type</th>
                                  <th className="text-left px-3 py-2 font-medium text-slate-600">Source</th>
                                </tr>
                              </thead>
                              <tbody>
                                {bcSchema.entities?.find(e => e.name === selectedBcEntity)?.fields
                                  ?.filter(field => !bcFieldSearch || field.name.toLowerCase().includes(bcFieldSearch.toLowerCase()))
                                  .map((field, idx) => (
                                  <tr
                                    key={field.name}
                                    onClick={() => handleBcFieldSelect(field)}
                                    className={`border-b border-slate-100 cursor-pointer transition-colors ${
                                      selectedBcField?.name === field.name
                                        ? "bg-[#00B7C3]/10 hover:bg-[#00B7C3]/15"
                                        : "hover:bg-slate-50/50"
                                    }`}
                                  >
                                    <td className="px-3 py-1.5 text-slate-400 text-xs">{idx + 1}</td>
                                    <td className={`px-3 py-1.5 font-mono text-xs ${selectedBcField?.name === field.name ? "font-medium text-[#00B7C3]" : ""}`}>
                                      {field.name}
                                    </td>
                                    <td className="px-3 py-1.5 text-xs text-slate-500">{field.type || "-"}</td>
                                    <td className="px-3 py-1.5">
                                      <Badge variant="outline" className="text-xs">
                                        {field.source || "api"}
                                      </Badge>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        {/* Selected field values */}
                        {selectedBcField && bcRawRecords?.records?.length > 0 && (
                          <div className="border rounded-md border-[#00B7C3]/30 bg-[#00B7C3]/5 p-3">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-sm font-medium text-[#00B7C3] flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-[#00B7C3]" />
                                Valeurs de « {selectedBcField.name} »
                              </h4>
                              <button
                                onClick={() => setSelectedBcField(null)}
                                className="text-slate-400 hover:text-slate-600"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                            <div className="text-xs text-slate-500 mb-2">
                              Type: {selectedBcField.type || "inconnu"} • Source: {selectedBcField.source || "api"}
                            </div>
                            <div className="max-h-[300px] overflow-y-auto">
                              <table className="w-full text-sm">
                                <thead className="sticky top-0 bg-white/80 backdrop-blur-sm">
                                  <tr className="border-b">
                                    <th className="text-left px-2 py-1 text-xs font-medium text-slate-500 w-8">#</th>
                                    <th className="text-left px-2 py-1 text-xs font-medium text-slate-500">Valeur</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {bcRawRecords.records.map((rec, idx) => (
                                    <tr key={idx} className="border-b border-slate-100">
                                      <td className="px-2 py-1.5 text-slate-400 text-xs">{idx + 1}</td>
                                      <td className="px-2 py-1.5 font-mono text-xs">
                                        {rec[selectedBcField.name] !== undefined && rec[selectedBcField.name] !== null && rec[selectedBcField.name] !== ""
                                          ? String(rec[selectedBcField.name])
                                          : <span className="text-slate-300 italic">vide</span>
                                        }
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {/* Sample records */}
                        <div>
                          <h4 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                            Aperçu des enregistrements
                            {bcRawLoading && <RefreshCw className="w-3.5 h-3.5 animate-spin text-slate-400" />}
                          </h4>
                          {bcRawRecords && bcRawRecords.records?.length > 0 ? (
                            <div className="max-h-[300px] overflow-auto border rounded-md">
                              <table className="text-xs" style={{ minWidth: `${(bcRawRecords.fields?.length || 0) * 120 + 50}px` }}>
                                <thead className="sticky top-0 bg-slate-50 border-b">
                                  <tr>
                                    <th className="text-left px-2 py-1.5 font-medium text-slate-600 sticky left-0 bg-slate-50 z-10">#</th>
                                    {bcRawRecords.fields?.map((f, i) => (
                                      <th key={i} className="text-left px-2 py-1.5 font-medium text-slate-600 whitespace-nowrap">
                                        {f}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {bcRawRecords.records.map((rec, idx) => (
                                    <tr key={idx} className="border-b border-slate-100">
                                      <td className="px-2 py-1 text-slate-400 sticky left-0 bg-white z-10">{idx + 1}</td>
                                      {bcRawRecords.fields?.map((f, i) => (
                                        <td key={i} className="px-2 py-1 whitespace-nowrap" title={String(rec[f] ?? "")}>
                                          {rec[f] !== undefined && rec[f] !== null && rec[f] !== ""
                                            ? String(rec[f]).length > 30 ? String(rec[f]).slice(0, 27) + "..." : String(rec[f])
                                            : <span className="text-slate-300">—</span>
                                          }
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : bcRawLoading ? (
                            <div className="py-4 text-center text-slate-400 text-sm">
                              Chargement...
                            </div>
                          ) : (
                            <div className="py-4 text-center text-slate-400 text-sm">
                              Aucun enregistrement trouvé
                            </div>
                          )}
                          {bcRawRecords && (
                            <p className="text-xs text-slate-400 mt-1">
                              {bcRawRecords.total_fields} champs au total • {bcRawRecords.count} enregistrement(s) affichés
                            </p>
                          )}
                        </div>
                      </CardContent>
                    </>
                  )}
                </Card>
              </div>
            </>
          )}

          {!bcSchemaLoading && !bcSchema && (
            <Card>
              <CardContent className="py-12 text-center">
                <AlertCircle className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p className="text-slate-500">Impossible de charger le schéma BC.</p>
                <p className="text-sm text-slate-400 mt-1">Vérifiez la connexion Business Central dans Settings.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
